import { useLocale, useT, useUnitSystem } from '@/i18n/react';
import { formatTemperature, formatWindSpeed } from '@/lib/format';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { WeatherIcon } from '@/components/WeatherIcon';
import { WeatherCityPicker } from '@/components/WeatherCityPicker';
import { useAuth } from '@/hooks/AuthProvider';
import { db } from '@/lib/firebase';
import { currentPosition } from '@/lib/currentPosition';
import { api, apiConfigured, type WeatherResult } from '@/lib/api';
import {
  formatPlaceTime,
  hasLocationPermission,
  pickWeatherPosition,
  placeKey,
  WEATHER_REFRESH_MS,
  type PositionSample,
  type WeatherPlaceChoice,
} from '@/lib/weatherPlace';
import { loadWeatherPlace, saveWeatherPlace } from '@/lib/weatherPlaceStore';
import './weatherWidget.css';

/**
 * Kompakt időjárás — a köszöntő sor jobb szélén, KIBONTHATÓAN.
 *
 * Zárva egyetlen ikon és a hőmérséklet, egymás mellett. (A referenciaképen
 * egymás alatt vannak; ez szándékos eltérés, Geri kérésére — a köszöntő sorba
 * így fér be anélkül, hogy megnőne a fejléc magassága.)
 *
 * KOPPINTÁSRA UGYANAZ A PANEL NŐ MEG BALRA, nem nyílik külön doboz: a
 * mérőszámok a pillen BELÜL jelennek meg, a bal szélen álló nyílhegy pedig
 * eleve elárulja, hogy van mit kinyitni. Három adat jön elő — csapadék
 * esélye, páratartalom, szél —, a hőmérséklet és az égkép NEM ismétlődik
 * meg, hiszen ott van mellettük a panel jobb szélén.
 *
 * Nyitva a „Szia, <név>" felirat elrejtőzik (`home.css`, `:has()`): a sáv
 * úgyis eltakarná, így viszont a hely is felszabadul neki.
 *
 * MELLETTE a hely és a pontos idő: „Budapest XI., 15:42 ⓘ" — a Home-on a
 * köszöntés alatt, balra (`home.css` rácsa helyezi el). Rákoppintva
 * kereshető városlista (`WeatherCityPicker`). A választott város a
 * `users/{uid}/private/settings.weatherPlace` mezőbe kerül; `null` = saját
 * helyzet (GRUNDO #61).
 *
 * FRISSÍTÉS (Geri, GRUNDO #61): appindításkor mindig; lehúzásra csak ha az
 * adat 15 percnél régebbi; egyébként magától 15 percenként.
 *
 * ⚠️ NEM KÉR HELYZETET MAGÁTÓL, amikor betölt az app.
 *
 * Ez a legfontosabb döntés a komponensben. Egy engedélykérő ablak az
 * indításkor, egy időjárás-csempéért, az egyik legbiztosabb módja annak, hogy
 * a felhasználó örökre megtagadja a helyzetét — és akkor a TÉRKÉP sem működik,
 * ami viszont a termék lényege. Ezért a saját helyzetnél:
 *
 *   1. ha a helyengedély MÁR megvan (kérdés nélkül kiderítve, lásd
 *      `hasLocationPermission`), friss, városnyi pontosságú helyzetet kérünk —
 *      így az időjárás oda szól, ahol most vagy;
 *   2. különben a `users/{uid}/private/position` dokumentumot olvassuk. Ezt a
 *      Grund képernyő már úgyis felírta, ha a felhasználó járt ott — ez
 *      viszont RÉGI is lehet (csak pontosabb méréssel vagy 24 óra után
 *      íródik felül), ezért látszik alatta a városnév;
 *   3. ha egyik sincs, a widget helyén egy KOPPINTHATÓ jel áll. A böngésző
 *      engedélykérése csak akkor jön elő, ha a felhasználó maga kérte.
 */

type Position = { lat: number; lon: number };

/**
 * Az utolsó sikeres betöltés — MODUL szinten, nem a komponensben.
 *
 * A Home képernyőről elnavigálva a widget lebomlik; visszatérve nem kell új
 * kérés, ha 15 percen belül vagyunk (Geri: „ha 15 percnél régebben nem
 * frissült"). Új appindítás = új modul = üres, tehát indításkor MINDIG friss.
 */
let lastLoad: {
  key: string;
  at: number;
  weather: WeatherResult;
  placeName: string | null;
} | null = null;

/** Ilyen sűrűn nézzük meg az órát — a sor ideje és a 15 perces frissítés is ebből él. */
const TICK_MS = 10_000;

/**
 * @param refreshKey A lehúzásra frissítés jele. Csak akkor kér újra, ha az
 * adat 15 percnél régebbi — egyébként a meglévő a friss.
 */
export function WeatherWidget({ refreshKey = 0 }: { refreshKey?: number }) {
  const t = useT();
  const locale = useLocale();
  const units = useUnitSystem();
  const { user } = useAuth();
  /** A választott város; `null` = saját helyzet, `undefined` = még olvassuk. */
  const [choice, setChoice] = useState<WeatherPlaceChoice | null | undefined>(undefined);
  const [weather, setWeather] = useState<WeatherResult | null>(null);
  const [placeName, setPlaceName] = useState<string | null>(null);
  /** Nem tudunk hova nyúlni: se választott város, se helyzet. */
  const [needsPosition, setNeedsPosition] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  /**
   * A TELJES doboz (pill + hely-sor) — a „kívülre koppintás" ehhez mér. Ha csak
   * a pillhez mérne, a nyitott sáv alatti hely-sorra koppintva a lenyomás már
   * bezárná a sávot, a záró animáció alatt a sor elcsúszna, és a felengedés
   * mellé esne: a városválasztó nem nyílna meg (mérve, 390 px, GRUNDO #61).
   */
  const wrap = useRef<HTMLDivElement | null>(null);
  /**
   * A NYITÁS/ZÁRÁS FÁZISA (Geri, #71: „darabos, nem folyamatos"). A logikai
   * `open` a szándék; a `phase` a látvány: a sáv MÉRT pixelszélességről mért
   * pixelszélességre nő balra, a belső sor végig a VÉGSŐ szélességén van
   * kirakva (a növekvő doboz csak felfedi — semmi nem tördelődik át), az
   * elemek pedig jobbról balra, egyenként úsznak be alulról. Záráskor fordítva,
   * és a rács csak a zsugorodás VÉGÉN áll vissza — így semmi nem ugrik.
   */
  const [phase, setPhase] = useState<'closed' | 'opening' | 'open' | 'closing'>('closed');
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  /** A csukott pill szélessége a nyitás pillanatában — ide zsugorodik vissza. */
  const closedWidth = useRef(0);
  /** Mikor jött az utolsó adat — 0, amíg semmi. */
  const loadedAt = useRef(0);
  /** Fut-e épp betöltés — az ütemező és a lehúzás ne indítson rá még egyet. */
  const busy = useRef(false);
  /**
   * A legutóbb indított betöltés sorszáma. Csak az ő eredménye érvényes: ha
   * betöltés közben várost váltanak, a régi (lassabb) válasz nem írhatja
   * felül az újat.
   */
  const sequence = useRef(0);
  const hasWeather = useRef(false);
  hasWeather.current = weather !== null;

  /* ── 1. A választott város — a saját beállításokból ─────────── */

  useEffect(() => {
    if (!user || !db) {
      setChoice(null);
      return;
    }
    let alive = true;
    void loadWeatherPlace(user.uid)
      .then((place) => {
        if (alive) setChoice(place);
      })
      .catch(() => {
        if (alive) setChoice(null);
      });
    return () => {
      alive = false;
    };
  }, [user]);

  /* ── 2. A saját helyzet — engedélykérés NÉLKÜL ───────────────── */

  const resolveHere = useCallback(async (): Promise<Position | null> => {
    /*
      Ha a helyengedély MÁR megvan, friss helyzetet kérünk: így az időjárás
      oda szól, ahol most vagy, nem oda, ahol a térkép utoljára járt. Városnyi
      pontosság elég, és a `maxAgeMs` miatt egy nemrég mért fix azonnal jó —
      nem kapcsol be a GPS a semmiért.

      ⚠️ Natívban SOHA nem a `navigator.geolocation` — lásd `currentPosition`.
    */
    let fresh: PositionSample | null = null;
    if (await hasLocationPermission()) {
      try {
        const fix = await currentPosition({ maxAgeMs: 600_000, timeoutMs: 10_000, purpose: 'időjárás' });
        fresh = { lat: fix.lat, lon: fix.lng, accuracyM: fix.accuracyM, at: fix.at };
      } catch {
        /* Nincs friss fix — jöhet a tárolt. */
      }
    }
    /*
      A TÁROLT helyzet (`users/{uid}/private/position`) — a telefon pontos
      mérése, amit a térkép oszt meg az eszközök között. Akkor is kell, ha van
      friss mérés: asztali gépen az IP-alapú becslés, és a telefoné a jobb
      (mérve: „Mándok" Budapest XI. helyett). A döntés: `pickWeatherPosition`.
    */
    let stored: PositionSample | null = null;
    if (user && db) {
      try {
        const snapshot = await getDoc(doc(db, 'users', user.uid, 'private', 'position'));
        const data = snapshot.data() as { lat?: number; lng?: number; accuracyM?: number; at?: number } | undefined;
        if (typeof data?.lat === 'number' && typeof data?.lng === 'number') {
          stored = {
            lat: data.lat,
            lon: data.lng,
            accuracyM: Number(data.accuracyM ?? 99_999),
            at: Number(data.at ?? 0),
          };
        }
      } catch {
        /* Olvasási hiba — mintha nem lenne. */
      }
    }
    const picked = pickWeatherPosition(fresh, stored, Date.now());
    return picked ? { lat: picked.lat, lon: picked.lon } : null;
  }, [user]);

  /* ── 3. Az időjárás lekérése ─────────────────────────────────── */

  const load = useCallback(
    async (override?: Position) => {
      if (choice === undefined || !apiConfigured) return;
      const mine = ++sequence.current;
      busy.current = true;
      try {
        const position = override ?? choice ?? (await resolveHere());
        if (mine !== sequence.current) return;
        if (position === null) {
          setNeedsPosition(true);
          return;
        }
        setNeedsPosition(false);
        // Városnevet csak a saját helyzethez kérünk: a választott városét tudjuk.
        const result = await api.weather(position.lat, position.lon, { place: choice === null });
        if (mine !== sequence.current) return;
        const name = choice ? choice.name : (result.place ?? null);
        lastLoad = { key: `${locale}:${placeKey(choice)}`, at: Date.now(), weather: result, placeName: name };
        loadedAt.current = lastLoad.at;
        setWeather(result);
        setPlaceName(name);
        setFailed(false);
      } catch {
        /*
          A HIÁNYZÓ ADAT ITT NEM HIBAÜZENET.

          Az időjárás dísz a köszöntő sorban, nem funkció. Ha a szolgáltató
          néma, a widget egyszerűen nincs ott — egy piros hibasáv a Home
          tetején rosszabb, mint a hiánya. Ha viszont már van mit mutatni, egy
          elbukott FRISSÍTÉS nem tünteti el: marad a korábbi adat.
        */
        if (mine === sequence.current && !hasWeather.current) setFailed(true);
      } finally {
        if (mine === sequence.current) busy.current = false;
      }
    },
    [choice, resolveHere, locale],
  );

  const latestLoad = useRef(load);
  latestLoad.current = load;

  /** Régi-e az adat — 15 percnél régebben jött, vagy még semmi. */
  const stale = () => loadedAt.current === 0 || Date.now() - loadedAt.current >= WEATHER_REFRESH_MS;

  // Első betöltés, és minden helyváltás után. A modul-szintű gyorsítótár
  // 15 percen belül újrahasznosítható, ha ugyanarról a helyről szól.
  useEffect(() => {
    if (choice === undefined) return;
    if (lastLoad && lastLoad.key === `${locale}:${placeKey(choice)}` && Date.now() - lastLoad.at < WEATHER_REFRESH_MS) {
      loadedAt.current = lastLoad.at;
      setWeather(lastLoad.weather);
      setPlaceName(lastLoad.placeName);
      return;
    }
    void load();
  }, [choice, load, locale]);

  // Lehúzásra frissítés: csak ha az adat már régi.
  const firstRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === firstRefreshKey.current) return;
    if (busy.current || (loadedAt.current > 0 && !stale())) return;
    void latestLoad.current();
  }, [refreshKey]);

  /*
    Az ÓRA és az automatikus frissítés egy ütemre: a sor ideje percre pontos
    marad, és ha 15 perc eltelt, jön az új adat. Háttérből visszatérve (az
    időzítők ott állnak) a láthatóvá váláskor azonnal ellenőrzünk.
  */
  useEffect(() => {
    function tick() {
      setNow(Date.now());
      if (!busy.current && loadedAt.current > 0 && stale()) void latestLoad.current();
    }
    const timer = window.setInterval(tick, TICK_MS);
    function onVisible() {
      if (document.visibilityState === 'visible') tick();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  /* ── Kérésre: a helymeghatározás (a tű ikonra koppintva) ───────── */

  const askPosition = useCallback(() => {
    void currentPosition({ maxAgeMs: 600_000, purpose: 'időjárás' })
      .then((fix) => latestLoad.current({ lat: fix.lat, lon: fix.lng }))
      .catch(() => setFailed(true));
  }, []);

  /* ── Városválasztás ──────────────────────────────────────────── */

  const selectPlace = useCallback(
    (next: WeatherPlaceChoice | null) => {
      setPickerOpen(false);
      if (placeKey(next) === placeKey(choice ?? null)) return;
      /*
        A választás a SAJÁT privát beállításokba kerül (`firestore.rules` →
        `private/{docId}`: csak a tulajdonos) — így a másik eszközén is ezt
        látja. Nem játékadat, a kliens írhatja.
      */
      if (user) void saveWeatherPlace(user.uid, next).catch(() => {});
      lastLoad = null;
      loadedAt.current = 0;
      setWeather(null);
      setPlaceName(null);
      setFailed(false);
      setChoice(next);
    },
    [choice, user],
  );

  /* ── 4. A nyitás és zárás animációja ─────────────────────────── */

  useLayoutEffect(() => {
    const button = toggleRef.current;
    if (!button) {
      setPhase(open ? 'open' : 'closed');
      return;
    }
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let frame = 0;
    const settle = (next: 'open' | 'closed') => {
      button.style.width = '';
      setPhase(next);
    };
    const onEnd = (event: TransitionEvent) => {
      if (event.target !== button || event.propertyName !== 'width') return;
      settle(open ? 'open' : 'closed');
    };

    if (open) {
      if (phase === 'open' || phase === 'opening') return;
      const from = button.offsetWidth;
      if (phase === 'closed') closedWidth.current = from;
      // A cél a köszöntő sor TELJES szélessége — ugyanaz, amit nyitva a `width: 100%` ad.
      const row = button.closest<HTMLElement>('.home__greet-row') ?? button.parentElement;
      const to = row?.clientWidth ?? from;
      button.style.setProperty('--weather-full', `${to}px`);
      if (reduceMotion) {
        setPhase('open');
        return;
      }
      button.style.width = `${from}px`;
      setPhase('opening');
      // Két képkocka: előbb a kiinduló szélesség kerüljön a képre, csak utána a cél.
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          button.style.width = `${to}px`;
        });
      });
    } else {
      if (phase === 'closed' || phase === 'closing') return;
      if (reduceMotion) {
        setPhase('closed');
        return;
      }
      button.style.width = `${button.offsetWidth}px`;
      setPhase('closing');
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          button.style.width = `${closedWidth.current}px`;
        });
      });
    }
    button.addEventListener('transitionend', onEnd);
    return () => {
      cancelAnimationFrame(frame);
      button.removeEventListener('transitionend', onEnd);
    };
    // A fázist szándékosan nem figyeljük: csak a szándék (open) váltása indít animációt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const expanded = phase !== 'closed';
  const showDetail = phase === 'opening' || phase === 'open';

  /* ── 5. A kibontott sáv bezárása ─────────────────────────────── */

  useEffect(() => {
    if (!open) return;
    /*
      Kívülre koppintva és Escape-re is záródik. A `pointerdown` (és nem
      `click`) azért kell, hogy a sáv már a következő koppintás ELEJÉN
      eltűnjön — különben a mögötte lévő gombot csak második nekifutásra
      lehetne eltalálni.
    */
    function onPointerDown(event: PointerEvent) {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (failed) return null;

  const pill = needsPosition ? (
    <button
      type="button"
      className="weather weather--ask"
      onClick={askPosition}
      aria-label={t.weatherWidget.ask}
      title={t.weatherWidget.show}
    >
      <PinIcon />
    </button>
  ) : weather === null ? (
    <span className="weather weather--pending" aria-hidden="true" />
  ) : (
        <button
          ref={toggleRef}
          type="button"
          className={`weather weather--toggle${expanded ? ' weather--open' : ''}${showDetail ? ' weather--show' : ''}${
            phase === 'opening' || phase === 'closing' ? ' weather--animating' : ''
          }`}
          aria-expanded={open}
          /* A képernyőolvasónak a leírás is jár — az ikon önmagában néma. */
          aria-label={units === 'metric' ? t.weatherWidget.detailsMetric(weather.description, weather.tempC) : t.weatherWidget.details(weather.description, formatTemperature(weather.tempC))}
          title={weather.description}
          onClick={() => setOpen((value) => !value)}
        >
          {/* Csak a nyílhegy: ennyi elég ahhoz, hogy legyen mire koppintani. */}
          <ChevronIcon />

          {/*
            KINYITVA A SÁV OLYAN SZÉLES, MINT AZ ALATTA LÉVŐ DOBOZ (a
            GRUND/AKTIVITÁS/SOROZAT kártya) — Geri kérése (2026-08-27). A négy
            adat (csapadék, páratartalom, szél, hőfok) a rendelkezésre álló
            HELY szerint oszlik szét — `justify-content: space-between` —, ezért
            a köztük lévő rés keskeny telefonon szűk, tágasabb nézetben nő,
            NEM a szöveg mérete változik.

            A hőfok+ikon ITT, a sávban belül IS megjelenik (lásd lent a
            `weather__now--inline`-t) — ez a 4. „megálló" a space-between
            sorban. A gombon kívül álló másolat (`--closed`) ilyenkor elrejtve,
            nehogy duplán látszódjon a fok.
          */}
          <span className="weather__detail" aria-hidden={!open}>
            {/* Csak akkor látszik, ha a sáv elég széles hozzá — lásd a
                konténer-lekérdezést a CSS-ben. */}
            {/* A `--i` a beúszás sorszáma JOBBRÓL: a sáv balra nő, az elemek ebben a sorrendben tűnnek elő. */}
            <span className="weather__label" aria-hidden="true" style={{ '--i': 4 } as CSSProperties}>
              {t.weatherWidget.title}
            </span>
            <Metric
              icon={<PrecipitationIcon />}
              tone="precip"
              order={3}
              label={t.weatherWidget.precipitation}
              value={weather.precipitationChance === null ? '–' : `${weather.precipitationChance}%`}
            />
            <Metric
              icon={<HumidityIcon />}
              tone="humidity"
              order={2}
              label={t.weatherWidget.humidity}
              value={weather.humidity === null ? '–' : `${weather.humidity}%`}
            />
            <Metric
              icon={<WindIcon />}
              tone="wind"
              order={1}
              label={t.weatherWidget.wind}
              value={weather.windKph === null ? '–' : formatWindSpeed(weather.windKph)}
            />
            <span className="weather__now weather__now--inline" aria-hidden="true" style={{ '--i': 0 } as CSSProperties}>
              <WeatherIcon condition={weather.condition} night={weather.night} />
              <span className="weather__temp">{formatTemperature(weather.tempC)}</span>
            </span>
          </span>

          <span className="weather__now weather__now--closed">
            <WeatherIcon condition={weather.condition} night={weather.night} />
            <span className="weather__temp">{formatTemperature(weather.tempC)}</span>
          </span>
        </button>
  );

  /*
    A HELY ÉS AZ IDŐ — „Budapest XI., 15:42 ⓘ" (Geri, GRUNDO #61). A Home-on
    a köszöntés alatt, balra — ezt a `home.css` rácsa intézi. Rákoppintva a városválasztó nyílik. Az idő a HELY idejében
    megy (`weather.timezone`), tehát egy választott távoli városnál az ottani
    óra látszik. Helyzet nélkül is ott a sor: várost engedély nélkül is lehet
    választani.
  */
  const placeText = needsPosition
    ? t.weatherWidget.city
    : weather === null
      ? null
      : `${placeName ?? t.weatherWidget.here}, ${formatPlaceTime(new Date(now), weather.timezone)}`;

  return (
    <div ref={wrap} className={`weather-box${open ? ' weather-box--open' : ''}`}>
      {pill}
      {placeText !== null ? (
        <button
          type="button"
          className="weather__place"
          aria-label={t.weatherWidget.location(placeText)}
          onClick={() => {
            setOpen(false);
            setPickerOpen(true);
          }}
        >
          <span className="weather__place-text">{placeText}</span>
          <InfoIcon />
        </button>
      ) : null}
      {pickerOpen ? (
        <WeatherCityPicker
          current={choice ?? null}
          onSelect={selectPlace}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
}

function Metric({
  icon,
  tone,
  label,
  value,
  order,
}: {
  icon: React.ReactNode;
  /** Az ikon színe — mérőszámonként más, hogy szét lehessen kapni őket. */
  tone: 'precip' | 'humidity' | 'wind';
  label: string;
  value: string;
  /** A beúszás sorszáma jobbról (#71). */
  order: number;
}) {
  return (
    <span className="weather__metric" title={label} style={{ '--i': order } as CSSProperties}>
      <span className={`weather__metric-icon weather__metric-icon--${tone}`} aria-hidden="true">
        {icon}
      </span>
      <span className="weather__metric-value">
        {/* A címke csak a képernyőolvasónak és az egérnek szól: a sávban
            négy adatnak kell elférnie egy telefon szélességében. */}
        <span className="weather__metric-label">{label}: </span>
        {value}
      </span>
    </span>
  );
}

/* ── Ikonok ───────────────────────────────────────────────────────── */

const metricIcon = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function PrecipitationIcon() {
  return (
    <svg {...metricIcon}>
      <path d="M7 15a4 4 0 0 1 .6-8A5 5 0 0 1 17 8.5a3.2 3.2 0 0 1 .3 6.5" />
      <path d="M9 18.5v1.5M13 18v2.5M17 18.5v1.5" />
    </svg>
  );
}

function HumidityIcon() {
  return (
    <svg {...metricIcon}>
      <path d="M12 3.5s5.5 6 5.5 9.5a5.5 5.5 0 1 1-11 0C6.5 9.5 12 3.5 12 3.5Z" />
    </svg>
  );
}

function WindIcon() {
  return (
    <svg {...metricIcon}>
      <path d="M3 8h10a2.5 2.5 0 1 0-2.5-2.5M3 12h14a2.5 2.5 0 1 1-2.5 2.5M3 16h7.5a2 2 0 1 1-2 2" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      className="weather__chevron"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14.5 5.5 8 12l6.5 6.5" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      className="weather__place-info"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 11v6" />
      <circle cx="12" cy="7.6" r="0.6" fill="currentColor" />
    </svg>
  );
}
